import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'

import { useAuth } from '../../AuthContext'
import {
  TRANSPORT_CONFIG_KEY,
  fetchHospitalZones,
  setHospitalZone,
  type HospitalZone,
} from '../../api/transportConfig'
import { SavedNote } from './SavedNote'
import { SectionState } from './SectionState'
import { errorMessage, retryUnlessForbidden } from './errorMessage'
import { INPUT } from './styles'

/**
 * Each partner hospital's fare zone (ADR-0066, amending spec §E.1). A hospital
 * with no zone cannot be a destination for Standard Ride or Patient Transport.
 * Choosing a zone saves it: the choice is the action.
 */
export function HospitalZonesSection(): ReactNode {
  const { token } = useAuth()
  const query = useQuery({
    queryKey: [...TRANSPORT_CONFIG_KEY, 'hospital-zones'],
    queryFn: () => fetchHospitalZones(token),
    enabled: Boolean(token),
    retry: retryUnlessForbidden,
  })
  const state = (
    <SectionState
      isPending={query.isPending}
      error={query.error}
      onRetry={() => void query.refetch()}
      what="hospital zones"
    />
  )
  if (!query.data) return state

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-ink-3">
        A hospital with no zone cannot be booked as a destination for Standard
        Ride or Patient Transport.
      </p>
      {query.data.length === 0 ? (
        <p className="text-sm text-ink-2">
          No partner hospitals are listed right now.
        </p>
      ) : (
        <ul className="flex flex-col divide-y divide-line-1 rounded-[12px] border border-line-1">
          {query.data.map((h) => (
            <ZoneRow key={h.hospital_id} hospital={h} />
          ))}
        </ul>
      )}
    </div>
  )
}

function ZoneRow({ hospital }: { hospital: HospitalZone }): ReactNode {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  const [problem, setProblem] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  // The choice a failed save was trying to make stays on screen with the error,
  // rather than snapping back to the stored zone.
  const [failedChoice, setFailedChoice] = useState<string | null>(null)
  const save = useMutation({
    mutationFn: (zone: number | null) =>
      setHospitalZone(token, hospital.hospital_id, zone),
    onSuccess: () => {
      setProblem(null)
      setFailedChoice(null)
      setSaved(true)
      void queryClient.invalidateQueries({ queryKey: TRANSPORT_CONFIG_KEY })
    },
    onError: (e, zone) => {
      setProblem(errorMessage(e))
      setFailedChoice(zone === null ? '' : String(zone))
    },
  })
  const id = `zone-${hospital.hospital_id}`
  const shown =
    failedChoice ?? (hospital.zone === null ? '' : String(hospital.zone))
  return (
    <li className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 text-sm">
      <label htmlFor={id} className="min-w-0 flex-1 font-medium text-ink-1">
        {hospital.name}
      </label>
      <select
        id={id}
        className={INPUT}
        value={shown}
        disabled={save.isPending}
        onChange={(e) => {
          setSaved(false)
          save.mutate(e.target.value === '' ? null : Number(e.target.value))
        }}
      >
        <option value="">Not set</option>
        <option value="1">Zone 1</option>
        <option value="2">Zone 2</option>
        <option value="3">Zone 3</option>
      </select>
      <SavedNote show={saved} />
      {problem && (
        <span role="alert" className="w-full text-xs text-bed-bad">
          {problem}
        </span>
      )}
    </li>
  )
}
